import { getMediaUrl } from "./api";

const YOUTUBE_EMBED_ORIGIN = "https://www.youtube-nocookie.com";

export const EXTERNAL_VIDEO_IFRAME_PROPS = {
  credentialless: "",
  allow:
    "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
  allowFullScreen: true,
};

function extractYoutubeId(url) {
  const host = url.hostname.replace(/^www\./, "");

  if (
    host === "youtube.com" ||
    host === "m.youtube.com" ||
    host === "youtube-nocookie.com"
  ) {
    const v = url.searchParams.get("v");
    if (v) return v.split(/[&#]/)[0];

    const embedMatch = url.pathname.match(/^\/embed\/([^/?#]+)/);
    if (embedMatch?.[1]) return embedMatch[1];

    const shorts = url.pathname.match(/^\/shorts\/([^/?#]+)/);
    if (shorts?.[1]) return shorts[1];
  }

  if (host === "youtu.be") {
    return url.pathname.split("/").filter(Boolean)[0]?.split(/[?#]/)[0] ?? "";
  }

  return "";
}

function toYoutubeEmbedUrl(id) {
  const clean = String(id ?? "").trim();
  if (!clean) return "";
  return `${YOUTUBE_EMBED_ORIGIN}/embed/${clean}`;
}

export function toEmbedUrl(u) {
  if (!u) return "";
  const s = String(u).trim();
  if (!s) return "";

  if (s.startsWith("http://") || s.startsWith("https://")) {
    try {
      const url = new URL(s);
      const host = url.hostname.replace(/^www\./, "");

      const youtubeId = extractYoutubeId(url);
      if (youtubeId) return toYoutubeEmbedUrl(youtubeId);

      if (host === "vimeo.com") {
        const id = url.pathname.split("/").filter(Boolean)[0];
        if (id) return `https://player.vimeo.com/video/${id}`;
      }
      if (host === "player.vimeo.com") return s;

      return s;
    } catch {
      return s;
    }
  }

  return getMediaUrl(s.startsWith("/") ? s : `/${s}`);
}

const VIDEO_FILE_RE = /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i;

const DOCUMENT_EXT_RE =
  /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|rtf|zip|rar|kmz|kml|csv)(\?|#|$)/i;

function isLikelyDocument(url) {
  const s = String(url ?? "").trim();
  if (!s) return false;
  return DOCUMENT_EXT_RE.test(s);
}

function isIframeEmbed(embedSrc) {
  return (
    /youtube(-nocookie)?\.com\/embed/i.test(embedSrc) ||
    /player\.vimeo\.com\/video/i.test(embedSrc)
  );
}

/**
 * Si la URL es un video embebible, devuelve { type: 'iframe'|'file', src }.
 */
export function getVideoEmbedInfo(url) {
  const raw = String(url ?? "").trim();
  if (!raw) return null;
  if (isLikelyDocument(raw)) return null;

  const embedSrc = toEmbedUrl(raw);
  if (!embedSrc) return null;
  if (isLikelyDocument(embedSrc)) return null;

  if (isIframeEmbed(embedSrc)) {
    return { type: "iframe", src: embedSrc, originalUrl: raw };
  }

  const fileCandidate = raw.startsWith("http") ? raw : embedSrc;
  if (VIDEO_FILE_RE.test(fileCandidate) || VIDEO_FILE_RE.test(embedSrc)) {
    const src = raw.startsWith("http") ? raw : embedSrc;
    return { type: "file", src, originalUrl: raw };
  }

  return null;
}
